import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { globalizeSelectors } from '../Utils';
import {
  createIncrementalDataReducer,
  incrementalDataType,
  clearResults,
} from '../factories/ApiCall';
import type {
  HistoryAction,
  HistoryCustomerOrderitemsPayload,
  HistoryLastOrderPayload,
  HistoryOrderitem,
  HistoryOrderitemsPayload,
  HistorySeek,
  HistoryState,
  MoveOrdersToCustomerPayload,
} from '../types/History';

/*
 * action types
 */

const types = {
  SEARCH_ORDERITEMS: 'HISTORY/SEARCH_ORDERITEMS',
  SEARCH_CUSTOMER_ORDERITEMS: 'HISTORY/SEARCH_CUSTOMER_ORDERITEMS',
  LAST_ORDERITEM: 'HISTORY/LAST_ORDERITEM',
  MOVE_ORDERS_TO_CUSTOMER: 'HISTORY/MOVE_ORDERS_TO_CUSTOMER',
  MOVE_ORDERS_TO_CUSTOMER_PENDING: 'HISTORY/MOVE_ORDERS_TO_CUSTOMER_PENDING',
} as const;

const mountPath = 'history'; // mount point in global state, must match root reducer

/*
 * action creators
 */

const actions = {
  searchOrderitems: (
    search?: string,
    start_date?: string,
    end_date?: string,
    custom_only?: boolean,
    local_only?: boolean,
    seek?: HistorySeek | null,
    include_hidden?: boolean
  ): HistoryAction<HistoryOrderitemsPayload> => ({
    type: types.SEARCH_ORDERITEMS,
    payload: {
      search,
      start_date,
      end_date,
      custom_only,
      local_only,
      seek,
      include_hidden,
    },
  }),

  searchCustomerOrderitems: (
    search?: string,
    start_date?: string,
    end_date?: string,
    custom_only?: boolean,
    local_only?: boolean,
    customerid?: number | null,
    seek?: HistorySeek | null
  ): HistoryAction<HistoryCustomerOrderitemsPayload> => ({
    type: types.SEARCH_CUSTOMER_ORDERITEMS,
    payload: {
      search,
      customerid,
      start_date,
      end_date,
      custom_only,
      local_only,
      seek,
    },
  }),
  clearCustomerOrderitems: (): HistoryAction => ({
    type: clearResults(types.SEARCH_CUSTOMER_ORDERITEMS),
  }),

  loadLastOrder: (): HistoryAction<HistoryLastOrderPayload> => ({
    type: types.LAST_ORDERITEM,
    payload: {},
  }),
  moveOrdersToCustomer: (
    orderids: number[],
    from_customerid: number | null,
    to_customerid: number | null
  ): HistoryAction<MoveOrdersToCustomerPayload> => ({
    type: types.MOVE_ORDERS_TO_CUSTOMER,
    payload: { orderids, from_customerid, to_customerid },
  }),
};

/*
 * state shape
 */
const propType = PropTypes.shape({
  orderitems: incrementalDataType,
  customer_orderitems: incrementalDataType,
  lastorder: incrementalDataType,
});

const initialState = { move_pending: false };

/*
 * reducer
 */
const reducer = combineReducers({
  orderitems: createIncrementalDataReducer<
    HistoryOrderitem,
    HistorySeek,
    HistoryOrderitemsPayload
  >(types.SEARCH_ORDERITEMS),
  customer_orderitems: createIncrementalDataReducer<
    HistoryOrderitem,
    HistorySeek,
    HistoryCustomerOrderitemsPayload
  >(types.SEARCH_CUSTOMER_ORDERITEMS),
  lastorder: createIncrementalDataReducer<
    HistoryOrderitem,
    HistorySeek,
    HistoryLastOrderPayload
  >(types.LAST_ORDERITEM),
  move_pending: (
    state: boolean = initialState.move_pending,
    action: HistoryAction
  ): boolean =>
    action.type === types.MOVE_ORDERS_TO_CUSTOMER_PENDING
      ? (action.payload as boolean)
      : state,
}) as (state: HistoryState | undefined, action: HistoryAction) => HistoryState;

/*
 * selectors
 */
const localSelectors = {
  orderitems: (state: HistoryState): HistoryState['orderitems'] =>
    state.orderitems,
  customer_orderitems: (
    state: HistoryState
  ): HistoryState['customer_orderitems'] => state.customer_orderitems,
  lastorder: (state: HistoryState): HistoryState['lastorder'] =>
    state.lastorder,
  move_pending: (state: HistoryState): boolean => state.move_pending,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
